<template>
  <CustomModal
    class="change-commission-level-modal"
    :is-modal-open="isOpen"
    @toggleModal="emit('closeModal')"
    @onClickAway="emit('closeModal')"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[540px] tw:md:w-[540px] tw:md:rounded-md tw:rounded-none"
  >
    <template #modalHeader
      ><p class="tw:text-xl tw:max-w-[90%]">{{ $t(title) }}</p></template
    >

    <div class="tw:flex tw:flex-col tw:w-full">
      <p class="tw:pb-6" v-html="$t(subtitle)" />
      <div class="tw:flex tw:items-center tw:justify-center tw:pb-6">
        <i18n-t keypath="rc_opportunities_commission_change" tag="p" scope="global">
          <template #FromPercentage>
            <span class="tw:font-bold tw:text-[32px] tw:mx-5"> {{ formatPercentage(language, currentCpa, 0, 0) }}</span>
          </template>
          <template #ToPercentage>
            <span class="tw:font-bold tw:text-[32px] tw:mx-5" :class="{ 'tw:text-red-700': currentCpa > newCpa }">
              {{ formatPercentage(language, newCpa, 0, 0) }}</span
            >
          </template>
        </i18n-t>
      </div>
      <div v-if="currentCpa < newCpa">
        <p class="tw:pb-6" v-html="$t('rc_opportunities_commission_change_info')" />
        <ul class="tw:list-disc tw:ml-6">
          <li v-for="item in upgradePoints" :key="item">
            <span>{{ $t(item) }}</span>
          </li>
        </ul>
        <div v-if="currentCpa === 10" class="tw:mt-6">
          <p>{{ $t('rc_commission_notification', { CommissionPercentage: lowestCommissionLevel }) }}</p>
        </div>
      </div>
      <div v-else>
        <p class="tw:pb-6" v-html="$t('rc_opportunities_commission_decrease_info')" />
        <ul class="tw:list-disc tw:ml-6 tw:pb-6">
          <li v-for="item in downgradePoints" :key="item">{{ $t(item) }}</li>
        </ul>
        <p>{{ $t('rc_opportunities_commission_decrease_confirm') }}</p>
      </div>
    </div>

    <template #modalFooter>
      <div
        class="tw:flex tw:w-full tw:items-start tw:md:items-center tw:md:flex-row tw:flex-col"
        :class="hasSteps ? 'tw:justify-between' : 'tw:justify-end'"
      >
        <div v-if="hasSteps" class="tw:md:w-1/2 tw:ml-1 tw:md:ml-0">
          <p class="tw:text-sm" v-if="currentStep === 1" v-html="$t('rc_opportunities_step_1')" />
          <p class="tw:text-sm" v-if="currentStep === 2" v-html="$t('rc_opportunities_step_2')" />
        </div>
        <div class="tw:flex tw:w-full tw:justify-between tw:md:justify-end tw:gap-x-4 tw:mt-4 tw:md:mt-0">
          <GhostButton class="tw:w-full tw:md:w-auto" @click="ghostButtonAction">{{
            $t('rc_opportunities_cta_close')
          }}</GhostButton>
          <MainButton class="tw:w-full tw:md:w-auto" @click="mainButtonAction" :disabled="isLoading">
            <span v-show="!isLoading" class="tw:text-nowrap">{{ $t('rc_opportunities_cta_confirm') }}</span>
            <span v-show="isLoading"> <SmallDotLoader color="#fff" /></span>
          </MainButton>
        </div>
      </div>
    </template>
  </CustomModal>
</template>
<script setup>
import { computed } from 'vue'

import CustomModal from '@/components/base/CustomModal.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'

import { useCampaignMutation, useCommissionLevels } from '@/components/rateConnect/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { formatPercentage } from '@/utils/Utility.ts'
import { useSessionValues } from '@/composables/useSessionValues'

import useToastNotifications from '@/components/notifications/useToastNotifications'

const emit = defineEmits(['closeModal'])
const { language } = useSessionValues()
const { eligibleCommissionLevels } = useCommissionLevels()
const { updateCommissionLevel } = useCampaignMutation()
const { displayNotification } = useToastNotifications()

const props = defineProps({
  isOpen: Boolean,
  currentCpa: Number,
  newCpa: Number,
})

const lowestCommissionLevel = computed(() => formatPercentage(language.value, eligibleCommissionLevels.value[0], 0, 0))
const upgradePoints = ['rc_opportunities_commission_benefit_2', 'rc_opportunities_commission_benefit_3']
const downgradePoints = [
  'rc_opportunities_commssion_decrease_info_1',
  'rc_opportunities_commission_decrease_info_2',
  'rc_opportunities_commission_decrease_info_3',
]
const title = computed(() => {
  if (props.currentCpa < props.newCpa) {
    return 'rc_opportunities_commission_increase_header'
  } else if (props.currentCpa > props.newCpa) {
    return 'rc_opportunities_commission_decrease_header'
  } else return ''
})

const subtitle = computed(() => {
  if (props.currentCpa < props.newCpa) return 'rc_opportunities_commission_increase_subtext'
  else return 'rc_opportunities_commission_decrease_subtext'
})

const mainButtonAction = () => {
  updateCommissionLevel(
    {
      accommodationId: selectedAccommodationId.value,
      commissionLevel: props.newCpa,
    },
    {
      onSuccess: () => {
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_cpa_commission_change_success',
          isTranslationKey: true,
          type: props.currentCpa < props.newCpa ? 'success' : 'info',
        })
        emit('closeModal')
      },
      onError: () => {
        displayNotification({
          message: 'something_went_wrong',
          isTranslationKey: true,
          type: 'error',
        })
        emit('closeModal')
      },
    }
  )
}

const ghostButtonAction = () => {
  emit('closeModal')
}
</script>
